import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import InteractiveViewer from './InteractiveViewer';
import { SOCKET_URL } from '../config';
import './DisplayPanel.css';

const socket = io(SOCKET_URL);

function DisplayPanel() {
  const [currentChapter, setCurrentChapter] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [bloodFlowEnabled, setBloodFlowEnabled] = useState(false);
  const [cardiacCyclePlaying, setCardiacCyclePlaying] = useState(false);
  const [activeCardiacPhase, setActiveCardiacPhase] = useState(null);
  const [visibleLayers, setVisibleLayers] = useState({
    chambers: true,
    valves: true,
    vessels: true,
    bloodFlow: false,
    labels: true,
    conduction: true
  });

  useEffect(() => {
    // Listen for state sync
    socket.on('state-sync', (state) => {
      setCurrentChapter(state.currentChapter);
      setCurrentTopic(state.currentTopic);
      setZoomLevel(state.zoomLevel);
      setPanX(state.panX);
      setPanY(state.panY);
      setActiveHotspot(state.activeHotspot);
      setBloodFlowEnabled(state.bloodFlowEnabled || false);
      setCardiacCyclePlaying(state.cardiacCyclePlaying || false);
      setActiveCardiacPhase(state.activeCardiacPhase);
      if (state.visibleLayers) {
        setVisibleLayers(state.visibleLayers);
      }
    });

    socket.on('chapter-selected', (chapter) => {
      setCurrentChapter(chapter);
      setCurrentTopic(null);
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
    });

    socket.on('topic-selected', (topic) => {
      setCurrentTopic(topic);
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
    });

    socket.on('zoom-updated', (data) => {
      setZoomLevel(data.level);
      setPanX(data.panX);
      setPanY(data.panY);
    });

    socket.on('pan-updated', (data) => {
      setPanX(data.x);
      setPanY(data.y);
    });

    socket.on('hotspot-selected', (hotspot) => {
      setActiveHotspot(hotspot);
      setZoomLevel(hotspot.zoom);
      setPanX(hotspot.x);
      setPanY(hotspot.y);
    });

    socket.on('view-reset', () => {
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
      setActiveHotspot(null);
    });

    socket.on('blood-flow-toggled', (enabled) => {
      setBloodFlowEnabled(enabled);
    });

    socket.on('cardiac-cycle-started', () => {
      setCardiacCyclePlaying(true);
    });

    socket.on('cardiac-cycle-paused', () => {
      setCardiacCyclePlaying(false);
    });

    socket.on('cardiac-phase-selected', (phaseId) => {
      setActiveCardiacPhase(phaseId);
      setCardiacCyclePlaying(false);
    });

    socket.on('layer-toggled', (data) => {
      setVisibleLayers(prev => ({ ...prev, [data.layer]: data.visible }));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <div className="display-panel">
      <div className="display-header">
        <h1>Digital Lab - Display Panel</h1>
        <p>Welham Boys School</p>
      </div>

      <div className="display-content">
        {currentTopic ? (
          <div className="display-viewer">
            <div className="topic-title">
              <h2>{currentTopic.title}</h2>
              {activeHotspot && (
                <div className="hotspot-label">
                  {activeHotspot.name}
                </div>
              )}
            </div>
            <InteractiveViewer
              topic={currentTopic}
              zoomLevel={zoomLevel}
              panX={panX}
              panY={panY}
              activeHotspot={activeHotspot}
              bloodFlowEnabled={bloodFlowEnabled}
              cardiacCyclePlaying={cardiacCyclePlaying}
              activeCardiacPhase={activeCardiacPhase}
              visibleLayers={visibleLayers}
              isTeacher={false}
            />
          </div>
        ) : currentChapter ? (
          <div className="chapter-waiting">
            <h2>{currentChapter.title}</h2>
            <p>Waiting for teacher to select a topic...</p>
          </div>
        ) : (
          <div className="waiting-screen">
            <h2>Ready for Presentation</h2>
            <p>Waiting for teacher to start...</p>
            <div className="status-indicator">
              <div className="pulse"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DisplayPanel;

